import { Contact } from '@eigtech/contacts-types'
import { isContactInactive, isContactRemoved } from '@eigtech/contacts-util'
import { getContactName, useGetCarrierBook } from '@eigtech/ui-shared-contacts'
import {
  FieldValues,
  FormFieldControl,
  FormFieldControlProps,
  WithFieldNameProp,
  useController,
} from '@eigtech/ui-shared-forms/shadcn'
import { Badge } from '@eigtech/ui/components/badge'
import {
  GroupBase,
  Props,
  Select,
  SelectComponentsConfig,
} from '@eigtech/ui/components/react-select'
import { cn } from '@eigtech/ui/lib/utils'
import { ReactNode, useMemo } from 'react'

export function CarrierSelect(
  props: React.ComponentProps<typeof Select<Contact, false, GroupBase<Contact>>>
) {
  const { data, isPending } = useGetCarrierBook()

  const contacts = useMemo(() => {
    const statusMap = {
      active: 0,
      inactive: 1,
      removed: 2,
    }

    return (data?.contacts ?? []).toSorted((a, b) => {
      const aStatus = isContactInactive(a)
        ? statusMap.inactive
        : isContactRemoved(a)
          ? statusMap.removed
          : statusMap.active
      const bStatus = isContactInactive(b)
        ? statusMap.inactive
        : isContactRemoved(b)
          ? statusMap.removed
          : statusMap.active

      return aStatus - bStatus
    })
  }, [data?.contacts])

  return (
    <Select
      {...props}
      components={selectComponents}
      getOptionLabel={(contact) => getContactName(contact)}
      getOptionValue={(contact) => contact.contactId}
      isLoading={isPending}
      isOptionDisabled={(contact) => isContactInactive(contact) || isContactRemoved(contact)}
      menuPortalTarget={document.body}
      options={contacts}
    />
  )
}

const selectComponents: SelectComponentsConfig<Contact, false, GroupBase<Contact>> = {
  Option: (props) => {
    const {
      className,
      data,
      label,
      innerRef,
      innerProps,
      getClassNames,
      isFocused,
      isDisabled,
      isSelected,
    } = props

    return (
      <div
        ref={innerRef}
        {...innerProps}
        aria-disabled={isDisabled ? true : undefined}
        aria-selected={isSelected}
        className={cn(
          getClassNames('option', props),
          innerProps.className,
          className,
          'flex flex-col gap-1'
        )}
        data-highlighted={isFocused ? true : undefined}
      >
        <p>{label}</p>
        {(isContactInactive(data) || isContactRemoved(data)) && (
          <div className="flex flex-row items-center gap-2">
            {isContactInactive(data) && <Badge variant="warning">Inactive</Badge>}
            {isContactRemoved(data) && <Badge variant="destructive">Removed</Badge>}
          </div>
        )}
      </div>
    )
  },
}

export type CarrierSelectFieldProps<TFieldValues extends FieldValues = FieldValues> =
  WithFieldNameProp<
    Omit<Props<Contact, false, GroupBase<Contact>>, keyof FormFieldControlProps>,
    TFieldValues
  > & {
    label: ReactNode
    fieldProps?: Omit<FormFieldControlProps, 'label' | 'name'>
  }

export function CarrierSelectField<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  fieldProps,
  ...selectProps
}: CarrierSelectFieldProps<TFieldValues>) {
  const {
    field,
    formState: { isSubmitting },
  } = useController({
    name,
    control: fieldProps?.control,
  })

  return (
    <FormFieldControl {...fieldProps} label={label} name={name}>
      <CarrierSelect {...field} isDisabled={isSubmitting} {...selectProps} />
    </FormFieldControl>
  )
}
